import { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

const STORAGE_KEY = 'systeza_theme'

const THEMES = {
  dark: {
    '--color-primary': '#1A6AF5',
    '--color-primary-dark': '#1251D4',
    '--color-accent': '#00C8E8',
    '--color-accent-dark': '#00A8C4',
    '--bg-base': '#020818',
    '--bg-surface': '#040E2A',
    '--bg-elevated': '#071640',
    '--border-subtle': '#0E1E4A',
    '--border-default': '#1A2F60',
    '--text-primary': '#EEF4FF',
    '--text-muted': '#7A90BC',
    '--text-dim': '#3A4F78',
  },
  light: {
    '--color-primary': '#1A6AF5',
    '--color-primary-dark': '#1251D4',
    '--color-accent': '#0097B2',
    '--color-accent-dark': '#007A94',
    '--bg-base': '#F5F7FA',
    '--bg-surface': '#FFFFFF',
    '--bg-elevated': '#FFFFFF',
    '--border-subtle': '#E2E8F0',
    '--border-default': '#CBD5E1',
    '--text-primary': '#0F172A',
    '--text-muted': '#475569',
    '--text-dim': '#94A3B8',
  },
}

function getInitialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') return stored
    if (stored === 'system') {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
    }
  } catch {}
  return 'dark'
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'dark' } catch { return 'dark' }
  })

  const resolvedTheme = preference === 'system'
    ? (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
    : preference

  // Apply CSS variables
  useEffect(() => {
    const vars = THEMES[resolvedTheme] || THEMES.dark
    const root = document.documentElement
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value)
    }
    root.style.setProperty('color-scheme', resolvedTheme)
    document.body.style.background = vars['--bg-base']
    document.body.style.color = vars['--text-primary']
  }, [resolvedTheme])

  // Listen for system theme changes
  useEffect(() => {
    if (preference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const handler = () => setPreference('system') // trigger re-render
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [preference])

  const setTheme = (theme) => {
    setPreference(theme)
    try { localStorage.setItem(STORAGE_KEY, theme) } catch {}
  }

  return (
    <ThemeContext.Provider value={{ theme: resolvedTheme, preference, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}
